import { MEMBER_ID } from "#features/conversations/utils/composerDocument.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { GetMembersByEmailsQueryMemberByEmail, GetMembersByIdsQueryMember } from "#types/types.ts";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const MEMBER_CARD_STALE_TIME = 5 * 60 * 1000;

// As many emails as the BFF looks up in one request.
const MAX_MEMBER_EMAILS = 50;

// One member's card, read from whatever request holds them: their own, or a batch already in flight.
export const memberCardQuery = (memberId: string, fetchMembers: (memberIds: string[]) => Promise<GetMembersByIdsQueryMember[]>) => ({
    queryKey: AiQueryKeys.GetMemberCard(memberId),
    queryFn: async () => (await fetchMembers([memberId])).find(member => member.id === memberId) ?? null,
    staleTime: MEMBER_CARD_STALE_TIME
});

// Who a mentioned member is, as their profile page's card shows it; null when the BFF doesn't return them.
export const useMemberCard = (memberId: string) => {
    const { getMembersByIds } = useAiApiClient();

    return useQuery({ ...memberCardQuery(memberId, getMembersByIds), enabled: MEMBER_ID.test(memberId) });
};

// Starts a member's lookup as soon as their card is wanted, so the card's open delay covers it.
export const usePrefetchMemberCard = () => {
    const queryClient = useQueryClient();
    const { getMembersByIds } = useAiApiClient();

    return useCallback((memberId: string) => {
        if (MEMBER_ID.test(memberId)) {
            void queryClient.prefetchQuery(memberCardQuery(memberId, getMembersByIds));
        }
    }, [getMembersByIds, queryClient]);
};

type FindMembersByEmails = (emails: string[]) => Promise<GetMembersByEmailsQueryMemberByEmail[]>;
type LoadMemberByEmail = (email: string, findMembers: FindMembersByEmails) => Promise<GetMembersByIdsQueryMember | null>;

interface WaitingLookup {
    resolve: (member: GetMembersByIdsQueryMember | null) => void;
    reject: (error: unknown) => void;
}

// Gathers the emails asked for within one tick into one request, so a conversation's named people are looked up
// together however many chips name them. The BFF answers each email as it was asked for.
const createMemberByEmailLoader = (): LoadMemberByEmail => {
    let waiting = new Map<string, WaitingLookup[]>();
    let findMembers: FindMembersByEmails = () => Promise.resolve([]);

    const lookUp = (batch: Map<string, WaitingLookup[]>, emails: string[]) => {
        Promise.resolve()
            .then(() => findMembers(emails))
            .then(found => {
                const members = new Map(found.map(({ email, member }) => [email, member]));
                emails.forEach(email => batch.get(email)?.forEach(({ resolve }) => resolve(members.get(email) ?? null)));
            })
            .catch((error: unknown) => emails.forEach(email => batch.get(email)?.forEach(({ reject }) => reject(error))));
    };

    const flush = () => {
        const batch = waiting;
        waiting = new Map();
        const emails = [...batch.keys()];
        for (let start = 0; start < emails.length; start += MAX_MEMBER_EMAILS) {
            lookUp(batch, emails.slice(start, start + MAX_MEMBER_EMAILS));
        }
    };

    return (email, find) => new Promise((resolve, reject) => {
        findMembers = find;
        if (waiting.size === 0) {
            setTimeout(flush);
        }
        waiting.set(email, [...(waiting.get(email) ?? []), { resolve, reject }]);
    });
};

// One per query client, the app's one shared cache: every component builds its own API client, so gathering by client
// would send a request per chip.
const memberByEmailLoaders = new WeakMap<QueryClient, LoadMemberByEmail>();

const memberByEmailLoader = (queryClient: QueryClient) => {
    let loader = memberByEmailLoaders.get(queryClient);
    if (!loader) {
        loader = createMemberByEmailLoader();
        memberByEmailLoaders.set(queryClient, loader);
    }

    return loader;
};

// The member the assistant names by email, as their card shows them; null when no member of the organization holds
// the address. Finding them fills their card too, so it opens with no request of its own.
export const useMemberCardByEmail = (email: string, { isEnabled = true } = {}) => {
    const queryClient = useQueryClient();
    const { getMembersByEmails } = useAiApiClient();

    return useQuery({
        queryKey: AiQueryKeys.GetMemberCardByEmail(email),
        queryFn: async () => {
            const member = await memberByEmailLoader(queryClient)(email, getMembersByEmails);
            if (member) {
                queryClient.setQueryData(AiQueryKeys.GetMemberCard(member.id), member);
            }

            return member;
        },
        enabled: isEnabled,
        staleTime: MEMBER_CARD_STALE_TIME
    });
};
